import { useEffect, useRef, useState } from "react";
import {
  LocateFixed,
  Minus,
  Plus,
  Layers,
  MapPin,
  Compass,
} from "lucide-react";
import type {
  Cluster,
  SearchFilters,
  SearchResult,
  StoreMatch,
} from "@little-finds/core";
import { OpenStreetMapCanvas } from "./OpenStreetMapCanvas";
import { coordinateRegionForBounds } from "../mapViewport";
declare global {
  interface Window {
    mapkit: any;
    initLittleMap: () => void;
  }
}
let mapPromise: Promise<any> | null = null;
export function loadMapKit(): Promise<any> {
  if (window.mapkit?.Map) return Promise.resolve(window.mapkit);
  if (mapPromise) return mapPromise;
  mapPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.apple-mapkit.com/mk/6/mapkit.core.js";
    script.crossOrigin = "anonymous";
    script.async = true;
    script.dataset.libraries = "map,services,annotations";
    script.dataset.token = import.meta.env.VITE_MAPKIT_TOKEN;
    script.dataset.callback = "initLittleMap";
    window.initLittleMap = () => resolve(window.mapkit);
    script.onerror = () => {
      mapPromise = null;
      reject(new Error("Map unavailable"));
    };
    document.head.append(script);
    setTimeout(() => {
      if (!window.mapkit?.Map) {
        mapPromise = null;
        reject(new Error("Map connection timed out"));
      }
    }, 15000);
  });
  return mapPromise;
}
export function MapCanvas(props: Parameters<typeof AppleMapCanvas>[0]) {
  if (!import.meta.env.VITE_MAPKIT_TOKEN && !props.isDemo)
    return <OpenStreetMapCanvas {...props} />;
  return <AppleMapCanvas {...props} />;
}

function AppleMapCanvas({
  result,
  selected,
  onSelect,
  filters,
  onMove,
  onLocate,
  onCluster,
  isDemo,
}: {
  result: SearchResult;
  selected: string | null;
  onSelect: (s: StoreMatch) => void;
  filters: SearchFilters;
  onMove: (v: Partial<SearchFilters>) => void;
  onLocate: () => void;
  onCluster: (cluster: Cluster) => void;
  isDemo: boolean;
}) {
  const el = useRef<HTMLDivElement>(null),
    map = useRef<any>(null),
    [failed, setFailed] = useState(false),
    [satellite, setSatellite] = useState(false),
    [ready, setReady] = useState(false);
  const token = import.meta.env.VITE_MAPKIT_TOKEN;
  const lastRadius = useRef(filters.radius_miles);
  const userMoved = useRef(false);
  const latest = useRef({ onSelect, onMove, onCluster });
  latest.current = { onSelect, onMove, onCluster };
  useEffect(() => {
    let cancelled = false;
    if (!token || isDemo) return;
    loadMapKit()
      .then((mk) => {
        if (cancelled || !el.current) return;
        const m = new mk.Map(el.current, {
          center: new mk.Coordinate(
            filters.center.latitude,
            filters.center.longitude,
          ),
          cameraDistance: filters.radius_miles * 2600,
          mapType: mk.MapType?.MutedStandard ?? "mutedStandard",
          colorScheme: mk.ColorScheme?.Light ?? "light",
          showsMapTypeControl: false,
        });
        map.current = m;
        lastRadius.current = filters.radius_miles;
        // Programmatic changes (including list drilldown) must not replace
        // the requested search bounds with the map's aspect-ratio adjustment.
        m.addEventListener("scroll-start", () => {
          userMoved.current = true;
        });
        m.addEventListener("zoom-start", () => {
          userMoved.current = true;
        });
        m.addEventListener("region-change-end", () => {
          if (!userMoved.current) return;
          userMoved.current = false;
          const r = m.region;
          latest.current.onMove({
            center: {
              latitude: r.center.latitude,
              longitude: r.center.longitude,
            },
            bounds: {
              north: r.center.latitude + r.span.latitudeDelta / 2,
              south: r.center.latitude - r.span.latitudeDelta / 2,
              east: r.center.longitude + r.span.longitudeDelta / 2,
              west: r.center.longitude - r.span.longitudeDelta / 2,
            },
            zoom: Math.max(1, Math.log2(360 / r.span.longitudeDelta)),
          });
        });
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      map.current?.destroy();
      map.current = null;
    };
  }, [token, isDemo]);
  useEffect(() => {
    if (!ready || !map.current) return;
    map.current.mapType = satellite
      ? (window.mapkit.MapType?.Hybrid ?? "hybrid")
      : (window.mapkit.MapType?.MutedStandard ?? "mutedStandard");
  }, [satellite, ready]);
  useEffect(() => {
    const m = map.current,
      mk = window.mapkit;
    if (!m || !mk) return;
    m.removeAnnotations(m.annotations);
    const pins = result.clusters.length
      ? result.clusters.map((c) => {
          const a = new mk.MarkerAnnotation(
            new mk.Coordinate(c.latitude, c.longitude),
            {
              title: `${c.count} places`,
              glyphText: String(c.count),
              color: "#59735f",
            },
          );
          a.addEventListener("select", () => latest.current.onCluster(c));
          return a;
        })
      : result.stores.map((s) => {
          const a = new mk.MarkerAnnotation(
            new mk.Coordinate(s.latitude, s.longitude),
            {
              title: s.name,
              color: s.id === selected ? "#d67c60" : "#59735f",
              glyphText: String(
                s.matches.filter((v) => v.state === "recent").length || "·",
              ),
            },
          );
          a.addEventListener("select", () => latest.current.onSelect(s));
          return a;
        });
    m.addAnnotations(pins);
  }, [result, selected, ready]);
  useEffect(() => {
    if (map.current && window.mapkit) {
      if (filters.bounds) {
        const region = coordinateRegionForBounds(filters.bounds);
        map.current.region = new window.mapkit.CoordinateRegion(
          new window.mapkit.Coordinate(
            region.center.latitude,
            region.center.longitude,
          ),
          new window.mapkit.CoordinateSpan(
            region.span.latitudeDelta,
            region.span.longitudeDelta,
          ),
        );
        lastRadius.current = filters.radius_miles;
        return;
      }
      const c = map.current.center;
      if (
        Math.abs(c.latitude - filters.center.latitude) > 0.00001 ||
        Math.abs(c.longitude - filters.center.longitude) > 0.00001
      )
        map.current.center = new window.mapkit.Coordinate(
          filters.center.latitude,
          filters.center.longitude,
        );
      if (lastRadius.current !== filters.radius_miles) {
        map.current.cameraDistance = filters.radius_miles * 2600;
        lastRadius.current = filters.radius_miles;
      }
    }
  }, [filters.center, filters.radius_miles, filters.bounds, ready]);
  if (failed && !isDemo)
    return (
      <OpenStreetMapCanvas
        result={result}
        selected={selected}
        onSelect={onSelect}
        filters={filters}
        onMove={onMove}
        onLocate={onLocate}
        onCluster={onCluster}
      />
    );
  const minLat = Math.min(...result.stores.map((s) => s.latitude), 41.898),
    maxLat = Math.max(...result.stores.map((s) => s.latitude), 41.945),
    minLon = Math.min(...result.stores.map((s) => s.longitude), -87.687),
    maxLon = Math.max(...result.stores.map((s) => s.longitude), -87.639);
  return (
    <section className="map-canvas" aria-label="Location map">
      <div ref={el} className="real-map" />
      {(isDemo || !token || failed) && (
        <>
          <svg
            className="illustrated-map"
            viewBox="0 0 1000 690"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="blocks"
                width="97"
                height="78"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(-12)"
              >
                <rect width="97" height="78" fill="#f0eee5" />
                <rect
                  x="8"
                  y="9"
                  width="79"
                  height="59"
                  rx="5"
                  fill="#e8e6dc"
                />
                <path d="M0 0h97M0 0v78" stroke="#fffdf6" strokeWidth="11" />
              </pattern>
            </defs>
            <rect width="1000" height="690" fill="url(#blocks)" />
            <path
              d="M710-70Q635 125 725 270T784 500Q761 635 833 750H1050V-70Z"
              fill="#c7dce0"
            />
            <path
              d="M726-70Q655 122 740 267T803 503Q782 635 849 750"
              fill="none"
              stroke="#b4cfd2"
              strokeWidth="4"
            />
            <path
              d="M340-20Q325 145 425 263T365 510L299 720"
              stroke="#c8dbde"
              strokeWidth="30"
              fill="none"
            />
            <path
              d="M-20 430 790 206M128-40 544 760M-50 172 739 422"
              stroke="#d5c7a2"
              strokeWidth="19"
              fill="none"
            />
            <path
              d="M-20 430 790 206M128-40 544 760M-50 172 739 422"
              stroke="#fff8dd"
              strokeWidth="12"
              fill="none"
            />
            <path d="m485 81 133-27 40 132-133 26Z" fill="#cdd9bd" />
            <path d="m74 410 142-27 39 122-138 28Z" fill="#d4ddc5" />
            <path d="m599 475 128-26 47 157-136 24Z" fill="#d1ddbf" />
            {isDemo && (
              <g
                fill="#9b9f93"
                fontFamily="Arial, sans-serif"
                fontSize="13"
                letterSpacing="3"
              >
                <text x="399" y="144" transform="rotate(-12 399 144)">
                  LINCOLN PARK
                </text>
                <text x="122" y="314" transform="rotate(-12 122 314)">
                  BUCKTOWN
                </text>
                <text x="354" y="470" transform="rotate(-12 354 470)">
                  WICKER PARK
                </text>
                <text x="607" y="608" transform="rotate(-12 607 608)">
                  OLD TOWN
                </text>
              </g>
            )}
            {isDemo && (
              <text
                x="887"
                y="390"
                fontFamily="Georgia,serif"
                fill="#829ea4"
                fontSize="20"
                letterSpacing="4"
                transform="rotate(78 887 390)"
              >
                LAKE MICHIGAN
              </text>
            )}
            <g fill="#bdc9b1">
              <circle cx="571" cy="99" r="12" />
              <circle cx="598" cy="127" r="17" />
              <circle cx="121" cy="444" r="16" />
              <circle cx="157" cy="475" r="12" />
            </g>
          </svg>
          {isDemo &&
            result.stores.map((s, i) => (
              <button
                key={s.id}
                className={`map-pin ${selected === s.id ? "active" : ""} ${!s.recent_count ? "lead" : ""}`}
                style={{
                  left: `${15 + ((s.longitude - minLon) / (maxLon - minLon)) * 63}%`,
                  top: `${17 + ((maxLat - s.latitude) / (maxLat - minLat)) * 60}%`,
                }}
                onClick={() => onSelect(s)}
                aria-label={`${s.name}, ${s.recent_count} recent products`}
              >
                <MapPin size={20} />
                <span>{i + 1}</span>
                <em>{s.name}</em>
              </button>
            ))}
          {!isDemo && (
            <div className="map-unavailable">
              <Compass size={32} />
              <strong>
                {failed
                  ? "The map is taking a little nap."
                  : "Your next little find starts here."}
              </strong>
              <span>
                {failed
                  ? "You can still explore every place in the list."
                  : "Search a city, or explore a clearly labeled sample hunt."}
              </span>
              <a className="button small" href="?demo=1">
                Explore the demo
              </a>
            </div>
          )}
        </>
      )}
      <div className="map-label">
        <span className="status-dot" />
        {isDemo ? "ILLUSTRATED DEMO · CHICAGO" : "FIND YOUR NEXT LITTLE THING"}
      </div>
      <div className="map-controls">
        <button aria-label="Use my location" title="Near me" onClick={onLocate}>
          <LocateFixed size={19} />
        </button>
        <button
          aria-label="Zoom in"
          onClick={() => {
            if (map.current) {
              userMoved.current = true;
              map.current.cameraDistance /= 2;
            } else onMove({ zoom: (filters.zoom ?? 12) + 1 });
          }}
        >
          <Plus size={19} />
        </button>
        <button
          aria-label="Zoom out"
          onClick={() => {
            if (map.current) {
              userMoved.current = true;
              map.current.cameraDistance *= 2;
            } else onMove({ zoom: (filters.zoom ?? 12) - 1 });
          }}
        >
          <Minus size={19} />
        </button>
        <button
          aria-label={
            satellite ? "Show quiet street map" : "Show satellite map"
          }
          aria-pressed={satellite}
          title={
            !ready
              ? "Satellite view is available on the live map"
              : satellite
                ? "Show quiet street map"
                : "Show satellite map"
          }
          disabled={!ready}
          onClick={() => setSatellite((value) => !value)}
        >
          <Layers size={18} />
        </button>
      </div>
      <div className="map-caption">
        {isDemo
          ? "Sample shops & sightings · not real availability"
          : token && !failed
            ? `Apple Maps · ${satellite ? "Satellite" : "Quiet streets"}`
            : "Map preview · live data requires setup"}
      </div>
    </section>
  );
}
